// frontend/src/features/avaliacao/perguntas.ts
// Perguntas fixas do sistema de avaliação em etapas (10/30/60 dias)

export type TipoPergunta = 'unica' | 'multipla' | 'texto' | 'nota_0_10' | 'tabela_aderencia';

export interface Pergunta {
    chave: string;
    etapa: number;
    ordem: number;
    pergunta: string;
    tipo: TipoPergunta;
    opcoes: string[];
    subtitulo?: string;
}

// Tabela de aderência (etapa 10, pergunta 4)
const ADERENCIA_OPCOES = ['Escorregou muito', 'Escorregou levemente', 'Não escorregou', 'Não usei'];
const ADERENCIA_PISOS = ['Piso molhado (cerâmica)', 'Piso seco (cerâmica)', 'Asfalto', 'Grama/terra', 'Piso de pedra'];

export const PERGUNTAS: Pergunta[] = [
    // ============================================================
    // ETAPA 1 — 10 DIAS
    // ============================================================
    {
        chave: '10_1', etapa: 10, ordem: 1,
        pergunta: 'Ao calçar pela primeira vez, sentiu algum ponto de aperto, folga ou atrito? Onde?',
        tipo: 'multipla',
        opcoes: ['Sim, nos dedos', 'Sim, na lateral', 'Sim, no calcanhar', 'Sim, no peito do pé', 'Sim, em múltiplas regiões', 'Não senti nenhum'],
    },
    {
        chave: '10_1_desc', etapa: 10, ordem: 1,
        pergunta: 'Se respondeu Sim acima, descreva rapidamente:',
        tipo: 'texto',
        opcoes: [],
    },
    {
        chave: '10_2', etapa: 10, ordem: 2,
        pergunta: 'Precisou de algum período de "amaciamento" ou já estava confortável no primeiro uso?',
        tipo: 'unica',
        opcoes: ['Já estava confortável desde o primeiro uso', 'Precisou de 1 a 2 dias', 'Precisou de 3 a 5 dias', 'Precisou de mais de 5 dias', 'Nunca ficou totalmente confortável'],
    },
    {
        chave: '10_3', etapa: 10, ordem: 3,
        pergunta: 'O calçado apresenta algum barulho incomum?',
        tipo: 'unica',
        opcoes: ['Nenhum barulho', 'Estalo', 'Rangido', 'Som do solado ao dobrar', 'Outro'],
    },
    {
        chave: '10_3_desc', etapa: 10, ordem: 3,
        pergunta: 'Se Outro, descreva:',
        tipo: 'texto',
        opcoes: [],
    },
    {
        chave: '10_4', etapa: 10, ordem: 4,
        pergunta: 'Como você avalia a aderência da sola nos pisos que usou?',
        tipo: 'tabela_aderencia',
        opcoes: ADERENCIA_OPCOES,
        subtitulo: 'Tipo de piso',
    },
    {
        chave: '10_5', etapa: 10, ordem: 5,
        pergunta: 'O sistema de fechamento (cadarço, velcro, elástico) prende firme sem folgar sozinho?',
        tipo: 'unica',
        opcoes: ['Sim, firme o tempo todo', 'Sim, mas precisei reapertar 1x por uso', 'Sim, mas precisei reapertar várias vezes', 'Não, fica folgando o tempo todo', 'Não se aplica'],
    },
    {
        chave: '10_6', etapa: 10, ordem: 6,
        pergunta: 'Após 10 dias, há qualquer sinal visível de desgaste?',
        tipo: 'multipla',
        opcoes: ['Nenhum sinal', 'Desgaste leve no solado', 'Pequena descostura', 'Material riscado/manchado', 'Outro'],
    },
    {
        chave: '10_6_desc', etapa: 10, ordem: 6,
        pergunta: 'Se Outro, descreva:',
        tipo: 'texto',
        opcoes: [],
    },
    {
        chave: '10_7', etapa: 10, ordem: 7,
        pergunta: 'O tamanho está correto em relação à sua numeração habitual?',
        tipo: 'unica',
        opcoes: ['Sim, perfeito', 'Um pouco apertado', 'Um pouco folgado', 'Muito apertado', 'Muito folgado'],
    },
    {
        chave: '10_8', etapa: 10, ordem: 8,
        pergunta: 'O calçado é fácil de calçar e tirar sem auxílio de calçadeira?',
        tipo: 'unica',
        opcoes: ['Sim, muito fácil', 'Razoável', 'Difícil, mas possível', 'Muito difícil, preciso de ajuda'],
    },
    {
        chave: '10_9', etapa: 10, ordem: 9,
        pergunta: 'Em uma nota de 0 a 10 para conforto imediato, qual nota daria? (0 = péssimo, 10 = excelente)',
        tipo: 'nota_0_10',
        opcoes: [],
    },
    {
        chave: '10_10', etapa: 10, ordem: 10,
        pergunta: 'Você trocaria algo neste calçado com base nesses 10 dias?',
        tipo: 'multipla',
        opcoes: ['Nada, está ótimo', 'Sim, o material do solado', 'Sim, a palmilha', 'Sim, o sistema de fechamento', 'Sim, o forro interno', 'Sim, outro'],
    },
    {
        chave: '10_10_desc', etapa: 10, ordem: 10,
        pergunta: 'Se outro, descreva:',
        tipo: 'texto',
        opcoes: [],
    },

    // ============================================================
    // ETAPA 2 — 30 DIAS
    // ============================================================
    {
        chave: '30_1', etapa: 30, ordem: 1,
        pergunta: 'Comparado aos 10 dias, o calçado está:',
        tipo: 'unica',
        opcoes: ['Mais confortável', 'Igualmente confortável', 'Menos confortável'],
    },
    {
        chave: '30_2', etapa: 30, ordem: 2,
        pergunta: 'Por quê?',
        tipo: 'texto',
        opcoes: [],
    },
    {
        chave: '30_3', etapa: 30, ordem: 3,
        pergunta: 'Houve aparecimento de algum ponto de atrito, bolha ou calo que não existia antes?',
        tipo: 'multipla',
        opcoes: ['Não', 'Sim, nos dedos', 'Sim, no calcanhar', 'Sim, na lateral', 'Sim, múltiplos locais'],
    },
    {
        chave: '30_4', etapa: 30, ordem: 4,
        pergunta: 'O solado apresenta desgaste visível?',
        tipo: 'unica',
        opcoes: ['Nenhum desgaste', 'Desgaste leve, mas uniforme', 'Desgaste moderado', 'Desgaste severo', 'Desgaste irregular (um lado mais que o outro)'],
    },
    {
        chave: '30_4_desc', etapa: 30, ordem: 4,
        pergunta: 'Se irregular, qual região mais desgastada?',
        tipo: 'multipla',
        opcoes: ['Calcanhar externo', 'Calcanhar interno', 'Ponta', 'Lateral externa', 'Lateral interna'],
    },
    {
        chave: '30_5', etapa: 30, ordem: 5,
        pergunta: 'O material externo (couro, sintético, tecido) está:',
        tipo: 'unica',
        opcoes: ['Como novo', 'Levemente manchado/riscado/amassado', 'Moderadamente danificado', 'Muito danificado'],
    },
    {
        chave: '30_6', etapa: 30, ordem: 6,
        pergunta: 'O forro interno apresenta:',
        tipo: 'multipla',
        opcoes: ['Nenhum problema', 'Pequeno desfiamento', 'Bolinhas (pillings)', 'Rasgo pequeno', 'Rasgo grande'],
    },
    {
        chave: '30_7', etapa: 30, ordem: 7,
        pergunta: 'O calçado passou a ter odor diferente após 30 dias de uso frequente?',
        tipo: 'unica',
        opcoes: ['Sem odor', 'Odor leve normal de uso', 'Odor forte', 'Odor muito forte e persistente'],
    },
    {
        chave: '30_8', etapa: 30, ordem: 8,
        pergunta: 'Após uso em piso molhado ou levemente úmido, ele ainda oferece segurança?',
        tipo: 'unica',
        opcoes: ['Sim, mesma segurança do início', 'Sim, mas levemente pior', 'Perdeu bastante aderência', 'Ficou perigoso de usar'],
    },
    {
        chave: '30_9_horas', etapa: 30, ordem: 9,
        pergunta: 'Você usou o calçado por quantas horas seguidas no máximo?',
        tipo: 'unica',
        opcoes: ['Até 2h', '2h a 4h', '4h a 6h', '6h a 8h', 'Mais de 8h'],
    },
    {
        chave: '30_9_cansaco', etapa: 30, ordem: 9,
        pergunta: 'Nesse período máximo, você sentiu cansaço nos pés?',
        tipo: 'unica',
        opcoes: ['Nenhum cansaço', 'Leve', 'Moderado', 'Muito cansado'],
    },
    {
        chave: '30_10', etapa: 30, ordem: 10,
        pergunta: 'Comparado com outro calçado similar que você já teve, onde este é MELHOR?',
        tipo: 'multipla',
        opcoes: ['Conforto', 'Aderência', 'Durabilidade', 'Design', 'Leveza', 'Em nada – é pior em tudo'],
    },
    {
        chave: '30_11', etapa: 30, ordem: 11,
        pergunta: 'Você recomendaria este calçado para um amigo que precisa usar 8h em pé por dia?',
        tipo: 'unica',
        opcoes: ['Sim, sem dúvida', 'Sim, com ressalvas', 'Não recomendaria'],
    },

    // ============================================================
    // ETAPA 3 — 60 DIAS
    // ============================================================
    {
        chave: '60_1', etapa: 60, ordem: 1,
        pergunta: 'O calçado ainda está em condições de uso diário?',
        tipo: 'unica',
        opcoes: ['Sim, como novo', 'Sim, mas com desgaste visível', 'Ainda dá para usar, mas desconfortável', 'Não, precisa ser substituído'],
    },
    {
        chave: '60_2', etapa: 60, ordem: 2,
        pergunta: 'O solado desgastou de forma uniforme ou irregular?',
        tipo: 'unica',
        opcoes: ['Uniforme', 'Irregular – calcanhar mais desgastado', 'Irregular – ponta mais desgastada', 'Irregular – lateral mais desgastada', 'Já perdeu a aderência em piso liso'],
    },
    {
        chave: '60_3', etapa: 60, ordem: 3,
        pergunta: 'Apareceu alguma descostura ou descolamento?',
        tipo: 'unica',
        opcoes: ['Nenhum', 'Pequena descostura na parte superior', 'Descolamento parcial do solado', 'Descolamento total ou quase total'],
    },
    {
        chave: '60_4', etapa: 60, ordem: 4,
        pergunta: 'O amortecimento/estrutura interna ainda oferece o mesmo suporte do início?',
        tipo: 'unica',
        opcoes: ['Sim, igual', 'Um pouco menor', 'Muito menor', 'Praticamente nenhum suporte restante'],
    },
    {
        chave: '60_5', etapa: 60, ordem: 5,
        pergunta: 'O cadarço, ilhoses ou sistema de fechamento apresentaram danos?',
        tipo: 'multipla',
        opcoes: ['Nenhum', 'Cadarço desfiando', 'Ilhós deformado/rasgado', 'Velcro perdendo aderência', 'Elástico frouxo'],
    },
    {
        chave: '60_6', etapa: 60, ordem: 6,
        pergunta: 'Você sentiu alguma dor no pé, joelho ou coluna atribuída ao uso desse calçado?',
        tipo: 'multipla',
        opcoes: ['Nenhuma dor', 'Dor no pé (plantar, metatarso)', 'Dor no joelho', 'Dor no quadril', 'Dor na coluna/lombar'],
    },
    {
        chave: '60_7', etapa: 60, ordem: 7,
        pergunta: 'Considerando preço, durabilidade e conforto: você compraria esse modelo com seu próprio dinheiro?',
        tipo: 'unica',
        opcoes: ['Compraria com certeza', 'Talvez comprasse em promoção', 'Não compraria'],
    },
    {
        chave: '60_8', etapa: 60, ordem: 8,
        pergunta: 'Qual nota final (0 a 10) para DURABILIDADE? (0 = quebrou/rasgou muito rápido, 10 = durou como novo)',
        tipo: 'nota_0_10',
        opcoes: [],
    },
    {
        chave: '60_9', etapa: 60, ordem: 9,
        pergunta: 'Qual nota final (0 a 10) para CUSTO-BENEFÍCIO PERCEBIDO? (0 = não vale nada, 10 = valeu cada centavo)',
        tipo: 'nota_0_10',
        opcoes: [],
    },
    {
        chave: '60_10', etapa: 60, ordem: 10,
        pergunta: 'O que você diria para a marca melhorar neste modelo para uma próxima versão?',
        tipo: 'texto',
        opcoes: [],
    },
];

export const ADERENCIA_PISOS_LIST = ADERENCIA_PISOS;
export const ADERENCIA_OPCOES_LIST = ADERENCIA_OPCOES;

export const ETAPA_LABELS: Record<number | string, string> = {
    10: '10 dias',
    30: '30 dias',
    60: '60 dias',
    veredito: 'Veredito',
};

export const ETAPA_ORDEM: (number | string)[] = [10, 30, 60, 'veredito'];
